import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { i18n, pricingTierMatchesBillingMode } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

interface PricingTier {
  id: string;
  name: string;
  scope: 'GLOBAL' | 'CUSTOMER';
  kind: 'PER_DAY' | 'FIXED_MONTHLY';
  isDefault: boolean;
}

interface CustomerEditable {
  id: string;
  name: string;
  billingMode: 'PER_PALLET_DAY' | 'FIXED_MONTHLY';
  contactPerson: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  billingAddress: string | null;
  vatId: string | null;
  pricingTierId: string | null;
  customRateCents: number | null;
  notes: string | null;
}

export function EditCustomerDialog({
  customer,
  onClose,
  onSaved,
}: {
  customer: CustomerEditable;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(customer.name);
  const [billingMode, setBillingMode] = useState<'PER_PALLET_DAY' | 'FIXED_MONTHLY'>(
    customer.billingMode,
  );
  const [contactPerson, setContactPerson] = useState(customer.contactPerson ?? '');
  const [contactEmail, setContactEmail] = useState(customer.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(customer.contactPhone ?? '');
  const [billingAddress, setBillingAddress] = useState(customer.billingAddress ?? '');
  const [vatId, setVatId] = useState(customer.vatId ?? '');
  const [pricingTierId, setPricingTierId] = useState(customer.pricingTierId ?? '');
  // Inline rate in euros (WMS-14); empty = use the assigned plan.
  const [rate, setRate] = useState(
    customer.customRateCents != null ? (customer.customRateCents / 100).toString() : '',
  );
  const [notes, setNotes] = useState(customer.notes ?? '');
  const [tiers, setTiers] = useState<PricingTier[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<PricingTier[]>('/pricing-tiers')
      .then(setTiers)
      .catch(() => setTiers([]));
  }, []);

  // Switching billing mode invalidates a pricing tier of the other kind —
  // drop the selection so we never submit a mismatched pair.
  function onBillingModeChange(mode: 'PER_PALLET_DAY' | 'FIXED_MONTHLY') {
    setBillingMode(mode);
    const selected = tiers?.find((x) => x.id === pricingTierId);
    if (selected && !pricingTierMatchesBillingMode(selected.kind, mode)) {
      setPricingTierId('');
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api(`/customers/${customer.id}`, {
        method: 'PATCH',
        body: {
          name,
          billingMode,
          contactPerson: contactPerson.trim() || undefined,
          contactEmail: contactEmail.trim() || undefined,
          contactPhone: contactPhone.trim() || undefined,
          billingAddress: billingAddress.trim() || undefined,
          vatId: vatId.trim() || undefined,
          pricingTierId: pricingTierId || null,
          customRateCents: rate.trim() === '' ? null : Math.round(parseFloat(rate) * 100),
          notes: notes.trim() || undefined,
        },
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'pricing_kind_mismatch') {
        setError(t('pricing.kind_mismatch'));
      } else if (err instanceof ApiError && err.code === 'invalid_input') {
        setError(t('customer.create.invalid_input'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('customer.edit.title')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <Field label={t('customer.col.name')}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </Field>
        <Field label={t('customer.col.billing_mode')}>
          <select
            value={billingMode}
            onChange={(e) => onBillingModeChange(e.target.value as typeof billingMode)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          >
            <option value="PER_PALLET_DAY">{t('customer.billing.PER_PALLET_DAY')}</option>
            <option value="FIXED_MONTHLY">{t('customer.billing.FIXED_MONTHLY')}</option>
          </select>
        </Field>
        <Field
          label={
            billingMode === 'FIXED_MONTHLY'
              ? t('billing.custom_rate.month')
              : t('billing.custom_rate.day')
          }
        >
          <input
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder={t('billing.custom_rate.placeholder')}
            className="w-full rounded border border-border bg-background px-2 py-1.5 tabular-nums"
          />
          <p className="mt-1 text-xs text-muted-foreground">{t('billing.custom_rate.hint')}</p>
        </Field>
        {tiers && tiers.length > 0 ? (
          <Field label={t('customer.col.pricing_tier')}>
            <select
              value={pricingTierId}
              onChange={(e) => setPricingTierId(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            >
              <option value="">{t('customer.pricing_tier.none')}</option>
              {tiers
                .filter(
                  (tier) =>
                    pricingTierMatchesBillingMode(tier.kind, billingMode) ||
                    tier.id === pricingTierId,
                )
                .map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name}
                    {tier.isDefault ? ` (${t('customer.pricing_tier.default')})` : ''}
                  </option>
                ))}
            </select>
          </Field>
        ) : null}
        <Field label={t('customer.contact.person')}>
          <input
            value={contactPerson}
            onChange={(e) => setContactPerson(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t('customer.contact.email')}>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </Field>
          <Field label={t('customer.contact.phone')}>
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </Field>
        </div>
        <Field label={t('customer.col.billing_address')}>
          <textarea
            value={billingAddress}
            onChange={(e) => setBillingAddress(e.target.value)}
            rows={2}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </Field>
        <Field label={t('customer.col.vat_id')}>
          <input
            value={vatId}
            onChange={(e) => setVatId(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </Field>
        <Field label={t('customer.col.notes')}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </Field>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}
